import * as qs from 'qs';
import { useCallback } from 'react';
import type { NavigateOptions, To } from 'react-router';
import { generatePath, useNavigate, useSearchParams } from 'react-router';

import { sessionStorage } from '#src/lib/storage/sessionStorage.ts';

export const getRedirectUrl = (
  deeplink: string,
  searchParams: URLSearchParams,
  params?: object
) => {
  if (
    !(
      deeplink.startsWith('sorare://') ||
      decodeURIComponent(deeplink).startsWith('sorare://')
    )
  ) {
    return null;
  }
  const decodedDeeplink = decodeURIComponent(deeplink);
  const decodedDeeplinkUrl = decodedDeeplink.split('?')[0];
  const decodedDeeplinkParam = decodedDeeplink.split('?')[1];
  const deeplinkParam = qs.parse(decodedDeeplinkParam);
  const otherParams = qs.parse(decodeURIComponent(searchParams.toString()));
  delete otherParams.deeplink;

  const newParams = {
    ...deeplinkParam,
    ...otherParams,
    ...params,
  };

  const withoutParams = Object.keys(newParams).length === 0;

  if (withoutParams) {
    return decodedDeeplinkUrl;
  }

  return `${decodedDeeplinkUrl}?${qs.stringify(newParams, {
    encode: false,
  })}`;
};

export const useDeeplinkFromNativeApp = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const storedDeeplink = sessionStorage.getItem('deeplink');
  const deeplink = searchParams.get('deeplink') || storedDeeplink || '';
  const navigateWithDeeplink = useCallback(
    (
      path?: To | number,
      options?: NavigateOptions,
      params?: Record<string, string | null>
    ) => {
      const redirectUrl = getRedirectUrl(deeplink, searchParams, params);
      if (redirectUrl) {
        // eslint-disable-next-line no-restricted-properties
        window.location.href = redirectUrl; // even though we "navigate" here sometimes the app will send us back to this page
      } else if (path) {
        if (typeof path === 'object') {
          navigate(path);
        } else {
          // @ts-expect-error - params is not typed correctly when path is any
          navigate(generatePath(path as any, params || {}), options);
        }
      }
    },
    [deeplink, searchParams, navigate]
  );

  return { hasDeeplink: !!deeplink, navigateWithDeeplink };
};
